/**
 * AgentRotationOverrideSlideout — Stage 2c-3 (P0-IDENT).
 *
 * Compact slideout for installing or removing a per-agent rotation
 * override (SVID TTL + rotation window fraction). Reached from the
 * "Add override" / "Remove override" button in the Rotation Override
 * card on IdentityAgentDetailPageV2.
 *
 * Two modes:
 *   - mode="add"    — both fields are empty inputs; submit calls PUT
 *                     /api/v1/identity/agents/{id}/override with the
 *                     populated subset.
 *   - mode="remove" — confirm dialog; submit calls DELETE
 *                     /api/v1/identity/agents/{id}/override.
 *
 * Single-purpose slideout (rotation policy only) rather than a generic
 * "edit all agent overrides" panel. Keeps the UX focused — every other
 * agent-tier override (child policy, federation) gets its own slideout
 * when wired in a follow-up PR.
 */
import React, { useEffect, useState } from 'react';
import { Save, X as XIcon, AlertTriangle, Trash2 } from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { Pill } from '../Pill';
import {
  updateIdentityAgentOverride,
  resetIdentityAgentOverride,
} from '../../../services/api';

export function AgentRotationOverrideSlideout({
  isOpen,
  onClose,
  onSaved,
  agentId,
  agentName,
  mode = 'add',  // 'add' | 'remove'
}) {
  const [svidTtl, setSvidTtl] = useState('');
  const [rotation, setRotation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when slideout closes.
  useEffect(() => {
    if (isOpen) return;
    setSvidTtl(''); setRotation(''); setError(null);
  }, [isOpen]);

  const handleAdd = async () => {
    setError(null);
    const body = {};
    if (svidTtl) body.svidTtlSeconds = parseInt(svidTtl, 10);
    if (rotation) body.rotationWindowFraction = parseFloat(rotation);

    // Client-side validation.
    if (body.svidTtlSeconds != null && body.svidTtlSeconds <= 0) {
      setError('SVID TTL must be positive.');
      return;
    }
    if (body.rotationWindowFraction != null
        && (body.rotationWindowFraction <= 0 || body.rotationWindowFraction >= 1)) {
      setError('Rotation window fraction must be strictly between 0 and 1.');
      return;
    }
    if (Object.keys(body).length === 0) {
      setError('Set at least one field to install an override.');
      return;
    }

    setSaving(true);
    const res = await updateIdentityAgentOverride(agentId, body);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Save failed.');
      return;
    }
    if (typeof onSaved === 'function') onSaved(res?.data);
    onClose();
  };

  const handleRemove = async () => {
    setError(null);
    setSaving(true);
    const res = await resetIdentityAgentOverride(agentId);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Delete failed.');
      return;
    }
    if (typeof onSaved === 'function') onSaved(null);
    onClose();
  };

  const isRemove = mode === 'remove';

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={460}
      ariaLabel={isRemove ? 'Remove rotation override' : 'Add rotation override'}
      header={
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">
            {isRemove ? 'Remove rotation override' : 'Add rotation override'}
          </h2>
          <Pill tone={isRemove ? 'danger' : 'trust'} size="sm">
            {isRemove ? 'Destructive' : 'Override'}
          </Pill>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-v2-border-subtle">
          <Button variant="ghost" size="sm" iconLeft={<XIcon size={13} />} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {isRemove ? (
            <Button variant="danger" size="sm" iconLeft={<Trash2 size={13} />}
                    onClick={handleRemove} disabled={saving}>
              {saving ? 'Removing…' : 'Remove override'}
            </Button>
          ) : (
            <Button variant="primary" size="sm" iconLeft={<Save size={13} />}
                    onClick={handleAdd} disabled={saving}>
              {saving ? 'Saving…' : 'Install override'}
            </Button>
          )}
        </div>
      }
    >
      <div className="px-4 py-4 flex flex-col gap-4">
        {error && (
          <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-md px-3 py-2 text-[12.5px] text-v2-danger flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="text-[12.5px] text-v2-ink-secondary">
          Agent: <span className="text-v2-ink font-semibold">{agentName || agentId}</span>
        </div>

        {isRemove ? (
          <div className="bg-v2-warning-soft border border-v2-warning/30 rounded-v2-md px-3 py-2.5 text-[12.5px] text-v2-warning">
            Removing the override reverts this agent's rotation policy to inherit from
            its instance (or org default if no instance override exists). This is
            non-destructive — the override row is deleted but the agent's SVID lifecycle
            continues uninterrupted.
          </div>
        ) : (
          <>
            <div className="text-[11px] text-v2-ink-muted">
              Leave a field blank to inherit that setting. At least one field must be set.
            </div>
            <div>
              <label className="block text-[11px] text-v2-ink-secondary mb-1">
                SVID TTL (seconds)
              </label>
              <input type="number" min="1" value={svidTtl}
                     onChange={(e) => setSvidTtl(e.target.value)}
                     placeholder="900" className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]" />
              <div className="text-[10.5px] text-v2-ink-muted mt-1">
                Common values: 900 (15 min), 3600 (1 hour), 7200 (2 hours).
              </div>
            </div>
            <div>
              <label className="block text-[11px] text-v2-ink-secondary mb-1">
                Rotation window fraction
              </label>
              <input type="number" step="0.001" min="0.001" max="0.999"
                     value={rotation} onChange={(e) => setRotation(e.target.value)}
                     placeholder="0.667" className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]" />
              <div className="text-[10.5px] text-v2-ink-muted mt-1">
                Fraction of TTL at which rotation triggers. 0.667 = ⅔ TTL (default).
              </div>
            </div>
          </>
        )}
      </div>
    </Slideout>
  );
}

export default AgentRotationOverrideSlideout;
